import { useCallback, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import ChevronLeft from "../../../../src/resources/chevron-left";
import ChevronRight from "../../../../src/resources/chevron-right";
import P2TextLabel from "../../../../src/components/P2Text/P2TextLabel/P2TextLabel";
import P2TextInput from "../P2TextInput/P2TextInput";
import P2Alert from "../../../../src/components/P2Alert/P2Alert";
import P2Modal from "../../../../src/components/P2Modal/P2Modal";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import useViewportWidth from "../../../../src/components/P2Stepper/useViewportWidth";
function P2DateSelector(props) {
  const isDesktopView = useViewportWidth();
  const value = props.value;
  const maxInput = props.maxInput;
  const inputDisplayFormat = "DD/MM/yyyy";

  const [isDirty, setDirty] = useState(false);
  const [isFocus, setFocus] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    let timeoutID;
    timeoutID = window.setTimeout(() => {
      if (isDirty && isFocus) {
        setShowError(true);
      } else {
        setShowError(false);
      }
    }, 200);
    return () => {
      clearTimeout(timeoutID);
    };
  }, [isDirty, isFocus]);
  const getInitialDisplayValue = useCallback(() => {
    if (value === "9999-12-31") {
      return props.placeholder ?? "Date";
    } else if (value.length) {
      const date = moment(value);
      if (date.isValid()) {
        return date.format(inputDisplayFormat);
      } else {
        return value;
      }
    } else {
      return "";
    }
  }, [props, value]);

  const getUpdatingCurrentMonth = useCallback(() => {
    if (value) {
      return moment(value).format("MMMM yyyy");
    } else if (maxInput) {
      if (moment(maxInput).format("yyyy-MM-DD") > moment().format("yyyy-MM-DD")) {
        return moment().format("MMMM yyyy");
      } else {
        return moment(maxInput).format("MMMM yyyy");
      }
    } else {
      return moment().format("MMMM yyyy");
    }
  }, [value, maxInput]);

  const [displayValue, setDisplayValue] = useState(getInitialDisplayValue());
  const [openedOptions, setOpenedOptions] = useState("");
  const [currentLastYearOption, setCurrentLastYearOption] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(getUpdatingCurrentMonth());
  const [modalOpen, setModalOpen] = useState(false);

  const toggleModal = useCallback(() => {
    setModalOpen((prev) => !prev);
  }, [modalOpen]);

  if (isDesktopView) {
    return <DateSelector {...props} isDesktopview={true} displayValue={displayValue} setDisplayValue={setDisplayValue} inputDisplayFormat={inputDisplayFormat} openedOptions={openedOptions} setOpenedOptions={setOpenedOptions} currentLastYearOption={currentLastYearOption} setCurrentLastYearOption={setCurrentLastYearOption} currentMonth={currentMonth} setCurrentMonth={setCurrentMonth} getUpdatingCurrentMonth={getUpdatingCurrentMonth} isDirty={isDirty} setDirty={setDirty} isFocus={isFocus} setFocus={setFocus} showError={showError} setShowError={setShowError}/>;
  } else {
    const propsWithoutLabel = { ...props };
    delete propsWithoutLabel["label"];

    return (
      <>
        {" "}
        {props.label && (
          <P2TextLabel marginBottom="10px" label={props.label} required={props.required} hasTooltip={props.hasTooltip} tooltipContent={props.tooltipContent} />
        )}
        <div className="polymer-datepicker-input-mobile">
          <P2TextInput
            type="text"
            placeholder={props.placeholder ?? "Date"}
            value={displayValue}
            icon={<CalendarIcon disabled={props.disabled} />}
            disabled={props.disabled}
            onClick={() => {
              toggleModal();
            }}
            readOnly={true}
          />
        </div>
      {((props.forceError && !value) || (props.isError && showError)) && (
        <div style={{ marginTop: 24 }}>
          <P2Alert variant={"danger"} text={props.errorMsg} />
        </div>
      )}
        <div className="polymer-datepicker-modal-wrapper">
          <P2Modal
            variant="close-button"
            onCloseModal={() => {
              toggleModal();
              setTimeout(() => {
                setCurrentLastYearOption(new Date().getFullYear());
                setDisplayValue?.(value && moment(value).isValid() ? moment(value).format(inputDisplayFormat) : "");
                if (!moment(value).isValid()) {
                  props?.onChange("");
                }
                setCurrentMonth(getUpdatingCurrentMonth());
                setOpenedOptions("");
              },500)
            }}
            className="polymer-datepicker-mobile-modal"
            isOpen={modalOpen}
          >
            <DateSelector
              {...propsWithoutLabel}
              isDesktopview={false}
              toggleModal={toggleModal}
              displayValue={displayValue}
              setDisplayValue={setDisplayValue}
              openedOptions={openedOptions} 
              setOpenedOptions={setOpenedOptions} 
              currentLastYearOption={currentLastYearOption} 
              setCurrentLastYearOption={setCurrentLastYearOption}
              currentMonth={currentMonth} 
              setCurrentMonth={setCurrentMonth}
              getUpdatingCurrentMonth={getUpdatingCurrentMonth}
              inputDisplayFormat={inputDisplayFormat}
              isDirty={isDirty}
              setDirty={setDirty}
              isFocus={isFocus}
              setFocus={setFocus}
              showError={showError}
              setShowError={setShowError}
            />
          </P2Modal>
        </div>
      </>
    );
  }
}

function CalendarIcon({ disabled = false }) {
  const color = disabled ? "#AAAAAA" : "#575757";
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14 0H2C0.89543 0 0 0.89543 0 2V14C0 15.1046 0.89543 16 2 16H14C15.1046 16 16 15.1046 16 14V2C16 0.89543 15.1046 0 14 0ZM1 3.85714C1 3.38376 1.44772 3 2 3H14C14.5523 3 15 3.38376 15 3.85714V14.1429C15 14.6162 14.5523 15 14 15H2C1.44772 15 1 14.6162 1 14.1429V3.85714Z"
        fill={color}
      />
      <path d="M6.5 7C7.05228 7 7.5 6.55228 7.5 6C7.5 5.44772 7.05228 5 6.5 5C5.94772 5 5.5 5.44772 5.5 6C5.5 6.55228 5.94772 7 6.5 7Z" fill={color} />
      <path d="M9.5 7C10.0523 7 10.5 6.55228 10.5 6C10.5 5.44772 10.0523 5 9.5 5C8.94772 5 8.5 5.44772 8.5 6C8.5 6.55228 8.94772 7 9.5 7Z" fill={color} />
      <path d="M12.5 7C13.0523 7 13.5 6.55228 13.5 6C13.5 5.44772 13.0523 5 12.5 5C11.9477 5 11.5 5.44772 11.5 6C11.5 6.55228 11.9477 7 12.5 7Z" fill={color} />
      <path d="M3.5 10C4.05228 10 4.5 9.55228 4.5 9C4.5 8.44772 4.05228 8 3.5 8C2.94772 8 2.5 8.44772 2.5 9C2.5 9.55228 2.94772 10 3.5 10Z" fill={color} />
      <path d="M6.5 10C7.05228 10 7.5 9.55228 7.5 9C7.5 8.44772 7.05228 8 6.5 8C5.94772 8 5.5 8.44772 5.5 9C5.5 9.55228 5.94772 10 6.5 10Z" fill={color} />
      <path d="M9.5 10C10.0523 10 10.5 9.55228 10.5 9C10.5 8.44772 10.0523 8 9.5 8C8.94772 8 8.5 8.44772 8.5 9C8.5 9.55228 8.94772 10 9.5 10Z" fill={color} />
      <path
        d="M12.5 10C13.0523 10 13.5 9.55228 13.5 9C13.5 8.44772 13.0523 8 12.5 8C11.9477 8 11.5 8.44772 11.5 9C11.5 9.55228 11.9477 10 12.5 10Z"
        fill={color}
      />
      <path d="M3.5 13C4.05228 13 4.5 12.5523 4.5 12C4.5 11.4477 4.05228 11 3.5 11C2.94772 11 2.5 11.4477 2.5 12C2.5 12.5523 2.94772 13 3.5 13Z" fill={color} />
      <path d="M6.5 13C7.05228 13 7.5 12.5523 7.5 12C7.5 11.4477 7.05228 11 6.5 11C5.94772 11 5.5 11.4477 5.5 12C5.5 12.5523 5.94772 13 6.5 13Z" fill={color} />
      <path
        d="M9.5 13C10.0523 13 10.5 12.5523 10.5 12C10.5 11.4477 10.0523 11 9.5 11C8.94772 11 8.5 11.4477 8.5 12C8.5 12.5523 8.94772 13 9.5 13Z"
        fill={color}
      />
    </svg>
  );
}

function DateSelector({ maxInput, minInput, isError = false, forceError = false, value, isDesktopview = true, setDisplayValue, displayValue, inputDisplayFormat, openedOptions, setOpenedOptions, currentLastYearOption, setCurrentLastYearOption, currentMonth, setCurrentMonth, getUpdatingCurrentMonth, isDirty, setDirty, isFocus, setFocus, showError, ...props }) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const backtrack = 12;
  const rangeFn = useCallback((start, end, step = 1) => {
    const arr = [];
    for (let i = start; i <= end; i += step) {
      arr.push(i);
    }

    return arr;
  }, []);

  // const [isDirty, setDirty] = useState(false);
  // const [isFocus, setFocus] = useState(false);
  // const [showError, setShowError] = useState(false);

  const minimumYear = minInput ? moment(minInput).year() : null;
  const maximumYear = maxInput ? moment(maxInput).year() : null;
  const options = useMemo(() => {
    if (openedOptions === "months") {
      return months;
    } else if (openedOptions === "years") {
      return rangeFn(currentLastYearOption - backtrack + 1, currentLastYearOption);
    } else {
      return [];
    }
  }, [openedOptions, currentLastYearOption]);

  // useEffect(() => {
  //   let timeoutID;
  //   timeoutID = window.setTimeout(() => {
  //     if (isDirty && isFocus) {
  //       setShowError(true);
  //     } else {
  //       setShowError(false);
  //     }
  //   }, 200);
  //   return () => {
  //     clearTimeout(timeoutID);
  //   };
  // }, [isDirty, isFocus]);

  useEffect(() => {
    if (value === "9999-12-31") {
      setDisplayValue(props.placeholder ?? "Date");
    } else if (value === "") {
      setDisplayValue("");
    } else if (value){
      setDisplayValue(moment(value).format(inputDisplayFormat));
    }
  }, [value]);

  return (
    <div className={`polymer-dateinput-form ${openedOptions ? "polymer-hide-calendar" : ""}`}>
      {props.label && (
        <P2TextLabel marginBottom="10px" label={props.label} required={props.required} hasTooltip={props.hasTooltip} tooltipContent={props.tooltipContent} />
      )}
      <DatePicker
        customInput={
          <div style={{ height: "44px" }}>
            <P2TextInput
              type="text"
              placeholder={props.placeholder ?? "Date"}
              value={displayValue}
              icon={<CalendarIcon disabled={props.disabled} />}
              disabled={props.disabled}
              onChange={(e) => {
                if (!e) {
                  props.onChange?.("");
                  setDisplayValue?.("");
                  return;
                }

                if(e.length > 10){//max number of character is 10 for date
                  return;
                }

                const firstSlashIndex = e.indexOf("/")
                const lastSlashIndex = e.lastIndexOf("/");
                if(firstSlashIndex !== -1 && firstSlashIndex !== 2){
                  return;
                }

                if(lastSlashIndex !== -1 && lastSlashIndex !== 2 && lastSlashIndex !== 5){
                  return;
                }
                
                const reg = new RegExp('^[0-9/]+$');
                if(e.length > 0 && !reg.test(e)){//can only consists of number and slash
                  return;
                }
                
                let newDisplayValue = e;
                //add slash only when user keying in new number instead of deleting something from the string
                if(newDisplayValue.length > displayValue.length){
                  if(newDisplayValue.length === 2 || newDisplayValue.length === 5){
                    newDisplayValue = newDisplayValue + "/";
                  }else if(newDisplayValue.length === 3 || newDisplayValue.length === 6){
                    const lastChar = newDisplayValue[newDisplayValue.length - 1];
                    newDisplayValue = newDisplayValue.substring(0, newDisplayValue.length - 1) + "/" + lastChar;
                  }
                }
                setDisplayValue?.(newDisplayValue);

                const momentDate = moment(newDisplayValue, inputDisplayFormat);
                if (!momentDate.isValid()) {
                  return;
                }

                if (maxInput && momentDate.format("yyyy-MM-DD") > moment(maxInput).format("yyyy-MM-DD")) {
                  return;
                }

                if (minInput && momentDate.format("yyyy-MM-DD") < moment(minInput).format("yyyy-MM-DD")) {
                  return;
                }

                if (newDisplayValue.length !== 10) {
                  //check whether the value of date is length of 10 -> DD/MM/yyyy
                  return;
                }
                props.onChange?.(momentDate.format("yyyy-MM-DD"));
                setDirty(true);
                setCurrentMonth(momentDate.format("MMMM yyyy"));
              }}
              // onBlur={(e) => {
              //   setDisplayValue?.(e && moment(e).isValid() ? moment(e).format(inputDisplayFormat) : "");
              //   if (!moment(e).isValid()) {
              //     props?.onChange("");
              //     setCurrentMonth(moment().format("MMMM yyyy"));
              //   }
              // }}
            />
          </div>
        }
        onChangeRaw={(e) => {
          if(e.type === "change"){
            const momentDate = moment(e, inputDisplayFormat);
            if(!moment(e).isValid() || (maxInput && momentDate.format("yyyy-MM-DD") > moment(maxInput).format("yyyy-MM-DD")) || minInput && momentDate.format("yyyy-MM-DD") < moment(minInput).format("yyyy-MM-DD")){
              e.preventDefault();
            }
          }
        }}
        open={isDesktopview ? undefined : true}
        placeholderText={props.placeholder ?? "Date"}
        disabled={props.disabled}
        formatWeekDay={(nameOfDay) => nameOfDay.toString().substring(0, 1)}
        minDate={minInput ? moment(minInput).toDate() : undefined}
        maxDate={maxInput ? moment(maxInput).toDate() : undefined}
        dateFormat={"dd/MM/yyyy"}
        onSelect={(e) => {
          if (props.onChange) {
            props.onChange(e ? moment(e).format("yyyy-MM-DD") : "");
            setDisplayValue?.(moment(e).format(inputDisplayFormat));
            setDirty(true);
            setFocus(true);
            props?.toggleModal?.();
          }
        }}
        selected={moment(value).isValid() ? moment(value).toDate() : undefined}
        openToDate={moment(currentMonth, "MMMM yyyy").toDate()}
        onCalendarOpen={() => {
          setCurrentMonth(getUpdatingCurrentMonth());
          setFocus(true);
        }}
        onCalendarClose={() => {
          setCurrentLastYearOption(new Date().getFullYear());
          setDisplayValue?.(value && moment(value).isValid() ? moment(value).format(inputDisplayFormat) : "");
          if (!moment(value).isValid()) {
            props?.onChange("");
          }
          setOpenedOptions("");
        }}
        renderCustomHeader={({ decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled, changeMonth, changeYear }) => {
          if (!openedOptions) {
            return (
              <div className="polymer-datepicker-header" style={{ display: "flex", justifyContent: "space-between" }}>
                <button
                  className="polymer-next-prev-buttons"
                  disabled={prevMonthButtonDisabled}
                  aria-disabled={prevMonthButtonDisabled}
                  onClick={() => {
                    decreaseMonth();
                    setCurrentMonth(moment(currentMonth, "MMMM yyyy").startOf("months").subtract({ months: 1 }).format("MMMM yyyy"));
                  }}
                >
                  <ChevronLeft />
                </button>
                <div style={{ display: "flex", gap: "31px" }}>
                  <p
                    onClick={() => {
                      setOpenedOptions("months");
                    }}
                  >
                    {currentMonth.split(" ")[0]}
                  </p>
                  <p
                    onClick={() => {
                      setOpenedOptions("years");
                    }}
                  >
                    {currentMonth.split(" ")[1]}
                  </p>
                </div>
                <button
                  className="polymer-next-prev-buttons"
                  disabled={nextMonthButtonDisabled}
                  aria-disabled={nextMonthButtonDisabled}
                  onClick={() => {
                    increaseMonth();
                    setCurrentMonth(moment(currentMonth, "MMMM yyyy").startOf("months").add({ months: 1 }).format("MMMM yyyy"));
                  }}
                >
                  <ChevronRight />
                </button>
              </div>
            );
          } else {
            return (
              <>
                <div className="polymer-monthyear-selection__title">
                  {" "}
                  {openedOptions === "years" ? (
                    <button
                      disabled={minimumYear !== null && Number(options[0]) - 1 < minimumYear}
                      aria-disabled={minimumYear !== null && Number(options[0]) - 1 < minimumYear}
                      className="polymer-next-prev-buttons"
                      onClick={() => {
                        setCurrentLastYearOption(Number(options[0]) - 1);
                      }}
                    >
                      {" "}
                      <ChevronLeft />
                    </button>
                  ) : (
                    <div></div>
                  )}
                  <p>Select {openedOptions.substring(0, 1).toUpperCase() + openedOptions.substring(1, openedOptions.length - 1)}</p>
                  {openedOptions === "years" ? (
                    <button
                      disabled={maximumYear !== null && currentLastYearOption + 1 > maximumYear}
                      aria-disabled={maximumYear !== null && currentLastYearOption + 1 > maximumYear}
                      className="polymer-next-prev-buttons"
                      onClick={() => {
                        setCurrentLastYearOption(Number(options[options.length - 1]) + backtrack);
                      }}
                    >
                      {" "}
                      <ChevronRight />
                    </button>
                  ) : (
                    <div></div>
                  )}
                </div>
                <div className="polymer-monthyear-selection__options">
                  {options.map((item, _index) => (
                    <button
                      type="button"
                      style={{
                        fontWeight: 500,
                        backgroundColor: currentMonth.substring(0, 3) === item || Number(currentMonth.split(" ")[1]) === item ? "#0C8188" : "#FFFFFF",
                        color: currentMonth.substring(0, 3) === item || Number(currentMonth.split(" ")[1]) === item ? "#FFFFFF" : "#575757",
                      }}
                      className="polymer-monthyear-selection__option"
                      key={_index}
                      onClick={() => {
                        if (openedOptions === "months") {
                          setCurrentMonth(`${item} ${currentMonth.split(" ")[1]}`);
                          changeMonth(_index);
                        } else {
                          setCurrentMonth(`${currentMonth.split(" ")[0]} ${item}`);
                          changeYear(Number(item));
                        }
                        setTimeout(() => {
                          setOpenedOptions("");
                        }, 150);
                      }}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </>
            );
          }
        }}
      />
      {((forceError && !value) || (isError && showError)) && (
        <div style={{ marginTop: 24 }}>
          <P2Alert variant={"danger"} text={props.errorMsg} />
        </div>
      )}
    </div>
  );
}

export default P2DateSelector;

P2DateSelector.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  value: PropTypes.string,
  required: PropTypes.bool,
  onChange: PropTypes.func,
  name: PropTypes.string,
  hasTooltip: PropTypes.bool,
  tooltipContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  errorMsg: PropTypes.string,
  maxInput: PropTypes.string,
  minInput: PropTypes.string,
  forceError: PropTypes.bool,
};

P2DateSelector.defaultProps = {
  disabled: false,
  forceError: false,
  tooltipContent:
    "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.",
};
